import { useCallback } from 'react';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/index.tsx';
import { getAdditionalAttributes } from '@atlassian/jira-issue-create-analytics-common/src/services/index.tsx';
import { useSmartFieldsMachineLearning } from '../../../../src/common/utils/use-smart-fields-ml/index.tsx';
import { useIsGICPositioningEnabled } from '../../../../src/controllers/gic-positioning-enabled/main.tsx';
import type { ErrorAnalyticsType } from './types.tsx';

export const useIssueCreateErrorAnalyticsEvent = () => {
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	const issueCreateMeta = getAdditionalAttributes();
	const [
		{
			isSubTaskCreationOpen,
			isCreateLinkedIssue = false,
			triggerPointKey,
			product,
			actionSource,
			addChildWorkItemTriggerSource,
		},
	] = useTriggerIssueCreateModal();

	const [{ selectedProject, canCreateAnotherIssue }] = useFormContext();

	const [{ minimizableModalView }] = isGICPositioningEnabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useTriggerIssueCreateModal()
		: [{ minimizableModalView: undefined }];

	const { withMachineLearning } = useSmartFieldsMachineLearning();

	const fireErrorAnalyticsEvent = useCallback(
		(props: ErrorAnalyticsType) => {
			const { meta, error, statusCode, errorType } = props;

			fireErrorAnalytics({
				meta,
				error,
				attributes: {
					statusCode,
					errorType,
					isNewGIC: true,
					isCreatingAnotherIssue: canCreateAnotherIssue,
					isSubtask: isSubTaskCreationOpen,
					triggerPointKey,
					projectType: selectedProject?.projectType,
					...(selectedProject ? { nextGenProject: selectedProject.isSimplified } : {}),
					...(isGICPositioningEnabled() ? { gicViewState: minimizableModalView } : {}),
					...(fg('create_linked_issues_trigger_point_issue_view') ? { isCreateLinkedIssue } : {}),
					smartFieldsEnabled: withMachineLearning,
					...(product ? { product } : {}),
					...(actionSource ? { actionSource } : {}),
					isValidationError: isValidationError(error),
					...issueCreateMeta,
					...(addChildWorkItemTriggerSource ? { addChildWorkItemTriggerSource } : {}),
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		[
			actionSource,
			canCreateAnotherIssue,
			isCreateLinkedIssue,
			isGICPositioningEnabled,
			isSubTaskCreationOpen,
			issueCreateMeta,
			minimizableModalView,
			product,
			selectedProject,
			triggerPointKey,
			withMachineLearning,
			addChildWorkItemTriggerSource,
		],
	);

	return { fireErrorAnalyticsEvent };
};
